import { z } from 'zod';
import { FileRequest, HostClipboardFiles, HostClipboardImage, HostClipboardText, HostFilesOffer, ImageRequest, TransferDone, TransferError, ViewerClipboardImage, ViewerClipboardText, ViewerFilesOffer, } from './transfer.js';
/**
 * Mensagens trocadas pelo DataChannel "input" durante uma sessão.
 *
 * Coordenadas de mouse são pixels da imagem de vídeo recebida pelo Viewer
 * (0..frameWidth, 0..frameHeight). O Host converte para a resolução real
 * do monitor capturado.
 */
export const MouseButton = z.enum(['left', 'right', 'middle']);
export const ButtonAction = z.enum(['down', 'up']);
export const MouseMove = z.object({
    type: z.literal('mouse_move'),
    x: z.number().finite(),
    y: z.number().finite(),
});
export const MouseButtonEvent = z.object({
    type: z.literal('mouse_button'),
    button: MouseButton,
    action: ButtonAction,
});
/** Deltas em "linhas" de rolagem, seguindo o sinal do WheelEvent do navegador. */
export const MouseScroll = z.object({
    type: z.literal('mouse_scroll'),
    deltaX: z.number().finite(),
    deltaY: z.number().finite(),
});
/**
 * `code` é a tecla física (KeyboardEvent.code, ex.: "KeyA", "ShiftLeft").
 * `key` é informativo (valor produzido no layout do Viewer).
 */
export const KeyboardEvent = z.object({
    type: z.literal('keyboard'),
    code: z.string().min(1).max(32),
    key: z.string().max(32),
    action: ButtonAction,
});
export const InputPing = z.object({
    type: z.literal('ping'),
    ts: z.number().int().nonnegative(),
});
/**
 * Sequência de Atenção Segura (Ctrl+Alt+Del). O navegador não consegue capturar essa
 * combinação, por isso vai como mensagem própria. Só tem efeito quando o Host roda como
 * serviço do sistema (tela de bloqueio e de login).
 */
export const CtrlAltDel = z.object({
    type: z.literal('ctrl_alt_del'),
});
/**
 * Preferências de uma conexão, escolhidas por quem acessa. Valem só para aquela sessão.
 * Campo novo: aqui, em DEFAULT_SESSION_SETTINGS e no Rust com `#[serde(default)]`.
 */
export const SessionSettings = z.object({
    /** O Host envia o som do computador junto com a tela. */
    transmitAudio: z.boolean(),
    /** Segundos sem a pessoa do Host mexer no mouse até o controle voltar para quem acessa. */
    guideHoldSeconds: z.number().int().min(1).max(30).default(1),
    /** Modo Bloqueio: o Host ignora o próprio mouse e teclado enquanto quem acessa controla. */
    blockLocalInput: z.boolean().default(false),
});
export const DEFAULT_SESSION_SETTINGS = {
    transmitAudio: false,
    guideHoldSeconds: 1,
    blockLocalInput: false,
};
/** Enviada quando o canal abre e a cada mudança; vale também em sessão somente visualização. */
export const SessionSettingsMessage = z.object({
    type: z.literal('session_settings'),
    settings: SessionSettings,
});
/** Viewer -> Host */
export const ViewerInputMessage = z.discriminatedUnion('type', [
    MouseMove,
    MouseButtonEvent,
    MouseScroll,
    KeyboardEvent,
    InputPing,
    CtrlAltDel,
    SessionSettingsMessage,
    ViewerClipboardText,
    ViewerClipboardImage,
    ViewerFilesOffer,
    FileRequest,
    ImageRequest,
    TransferDone,
    TransferError,
]);
export const InputPong = z.object({
    type: z.literal('pong'),
    ts: z.number().int().nonnegative(),
});
/** Um monitor do Host dentro do frame, em pixels do frame. O principal vem primeiro. */
export const ScreenMonitor = z.object({
    x: z.number().int().nonnegative(),
    y: z.number().int().nonnegative(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    primary: z.boolean(),
});
/**
 * Informa ao Viewer o tamanho do frame transmitido e da área capturada. O frame traz todos os
 * monitores do Host, cada um na sua posição; `monitors` diz onde fica cada um.
 */
export const ScreenInfo = z.object({
    type: z.literal('screen_info'),
    frameWidth: z.number().int().positive(),
    frameHeight: z.number().int().positive(),
    monitorWidth: z.number().int().positive(),
    monitorHeight: z.number().int().positive(),
    monitors: z.array(ScreenMonitor).max(16).optional(),
});
/** Imagem do cursor do Host. Vai uma vez por sessão para cada `id`; `rgba` é base64. */
export const CursorShape = z.object({
    type: z.literal('cursor_shape'),
    id: z.number().int().nonnegative(),
    width: z.number().int().positive().max(256),
    height: z.number().int().positive().max(256),
    hotspotX: z.number().int().nonnegative(),
    hotspotY: z.number().int().nonnegative(),
    rgba: z.string(),
});
/** Ponta do cursor do Host, em pixels da tela capturada. */
export const RemoteCursor = z.object({
    type: z.literal('cursor'),
    visible: z.boolean(),
    x: z.number().int(),
    y: z.number().int(),
    shapeId: z.number().int().nonnegative().optional(),
});
/**
 * A pessoa do Host está usando o mouse físico (modo guia): o mouse de quem acessa só move a
 * seta verde, sem clicar, rolar ou digitar, até ela ficar `guideHoldSeconds` sem mexer.
 */
export const MouseBlocked = z.object({
    type: z.literal('mouse_blocked'),
    blocked: z.boolean(),
});
/**
 * Estado real do Modo Bloqueio. `active` falso com o bloqueio pedido quer dizer que o Host
 * não tem permissão para bloquear (só funciona instalado como serviço ou como administrador).
 */
export const InputBlock = z.object({
    type: z.literal('input_block'),
    active: z.boolean(),
});
/** Host -> Viewer */
export const HostInputMessage = z.discriminatedUnion('type', [
    InputPong,
    ScreenInfo,
    CursorShape,
    RemoteCursor,
    MouseBlocked,
    InputBlock,
    HostClipboardText,
    HostClipboardFiles,
    HostClipboardImage,
    HostFilesOffer,
    TransferDone,
    TransferError,
]);
export const INPUT_CHANNEL_LABEL = 'input';
//# sourceMappingURL=input.js.map